import React from "react";
import { Link, useParams } from "react-router-dom";
import { serviceGalleryDetails } from "../../components/website/data/serviceGalleryData";
import "./styles/ServiceDetailsPage.css";
import { websitePaths } from "../../routes/websitePaths";

const ServiceGalleryPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const service = serviceGalleryDetails.find((item) => item.slug === slug);

  const workingSteps = [
    {
      step: "01",
      title: "Understand Your Requirement",
      description:
        "We first understand your space, your concern, and what result you want from the service.",
    },
    {
      step: "02",
      title: "Site Review And Planning",
      description:
        "Our team reviews the current condition, identifies the work scope, and plans the right execution approach.",
    },
    {
      step: "03",
      title: "Execution With Supervision",
      description:
        "The service is delivered with proper coordination, quality checks, and clear progress handling.",
    },
    {
      step: "04",
      title: "Final Handover",
      description:
        "Once the work is complete, we review the output and ensure the result is ready for practical use.",
    },
  ];

  if (!service) {
    return (
      <section className='service-gallery-page'>
        <div className='container py-5 text-center'>
          <h1 className='service-gallery-title mb-3'>Service Not Found</h1>
          <p className='service-gallery-subtitle mb-4'>
            The requested service details are unavailable right now.
          </p>
          <Link to={websitePaths.services} className='btn brand-btn-primary'>
            Back To Services
          </Link>
        </div>
      </section>
    );
  }

  const galleryImages = [
    ...service.beforeImages.map((image) => ({ ...image, tag: "Before" })),
    ...service.afterImages.map((image) => ({ ...image, tag: "After" })),
  ];

  return (
    <section className='service-gallery-page website-page'>
      <div className='service-gallery-hero website-page-hero py-5'>
        <div className='container text-center'>
          <p className='website-page-kicker mb-2'>Service Details</p>
          <h2 className='service-gallery-title website-page-title mb-3'>
            {service.title}
          </h2>
          <p className='service-gallery-subtitle website-page-subtitle mx-auto mb-0'>
            {service.summary}
          </p>
        </div>
      </div>

      <div className='container website-page-content'>
        <div className='service-process-layout mb-5'>
          <div className='service-process-head text-center'>
            <span className='website-section-kicker'>How We Work</span>
            <h2 className='website-section-title mb-2'>
              A simple step-by-step process that keeps the work easy to
              understand.
            </h2>
            <p className='website-section-subtitle mb-0'>
              We keep each stage clear so you know what starts first, what comes
              next, and how the work moves toward completion.
            </p>
          </div>

          <div className='row g-3 mt-1'>
            {workingSteps.map((item) => (
              <div className='col-md-6 col-xl-3' key={item.step}>
                <article className='service-process-step h-100'>
                  <span className='service-process-step-number'>
                    {item.step}
                  </span>
                  <h3>{item.title}</h3>
                  <p className='mb-0'>{item.description}</p>
                </article>
              </div>
            ))}
          </div>
        </div>

        <div className='service-gallery-block mb-4'>
          <div className='service-gallery-block-head text-center'>
            <span className='website-section-kicker'>
              Service Visual Journey
            </span>
            <h3 className='service-gallery-section-title mb-2'>
              Project images arranged in one clear gallery.
            </h3>
            <p className='mb-0'>
              These images help you compare the starting condition and the
              finished result in one place.
            </p>
          </div>
          <div className='row g-3 service-gallery-row'>
            {galleryImages.map((image, index) => (
              <div
                className='col-6 col-md-4 col-xl-3'
                key={`${image.src}-${index}`}
              >
                <article className='service-gallery-card h-100'>
                  <div className='service-gallery-media'>
                    <img
                      src={image.src}
                      alt={image.alt}
                      className='service-gallery-image'
                    />
                  </div>
                  <div className='service-gallery-card-body'>
                    <span
                      className={`service-gallery-badge${image.tag === "After" ? " service-gallery-badge--after" : ""}`}
                    >
                      {image.tag}
                    </span>
                    <p className='service-gallery-caption mb-0'>{image.alt}</p>
                  </div>
                </article>
              </div>
            ))}
          </div>
        </div>

        <div className='text-center'>
          <Link to={websitePaths.services} className='btn brand-btn-primary'>
            Back To All Services
          </Link>
        </div>

        <div className='service-gallery-cta mt-4'>
          <div>
            <span className='website-section-kicker'>Project Inquiry</span>
            <h3 className='mb-2'>
              Need this service for your home, office, shop, or site?
            </h3>
            <p className='mb-0'>
              Contact our team for requirement discussion, planning support, and
              the next practical step.
            </p>
          </div>
          <Link to={websitePaths.contact} className='btn brand-btn-primary'>
            Request Service Consultation
          </Link>
        </div>
      </div>
    </section>
  );
};

export default ServiceGalleryPage;
